/**
 * Parses a user ID from a JWT auth token without making a network request.
 * Only checks the `uid` claim — org-login tokens (issued before user selection)
 * intentionally omit `uid`, so a null return means this is an org-login token.
 * Returns null if the token is malformed or `uid` is absent.
 */
export function parseUserIdFromToken(token: string): number | null {
  try {
    const payloadB64 = token.split('.')[1];
    if (!payloadB64) {
      return null;
    }
    const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json);
    const raw = payload.uid;
    if (typeof raw === 'number') {
      return raw;
    }
    if (typeof raw === 'string') {
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Reads the authorized business id from a business/org JWT (org login before user pick).
 * Tries common claim names used across services (`bid` in push payloads, `businessId`, etc.).
 */
export function parseBusinessIdFromToken(token: string): number | null {
  try {
    const payloadB64 = token.split('.')[1];
    if (!payloadB64) {
      return null;
    }
    const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as Record<string, unknown>;
    const keys = ['bid', 'businessId', 'business_id'] as const;
    for (const key of keys) {
      const raw = payload[key];
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        return raw;
      }
      if (typeof raw === 'string') {
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
    }
    return null;
  } catch {
    return null;
  }
}
